import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DollarSign, Calendar, CheckCircle2, ArrowRight, Landmark, Clock } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

export default function StylistEarningsPage() {
  const [earnings, setEarnings] = useState(null);
  const [connect, setConnect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    load();
    // If we've just come back from Stripe onboarding, tidy the URL (status is refetched by load()).
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect')) {
      window.history.replaceState({}, '', '/stylist/earnings');
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.allSettled([
        api.get('/stylist/earnings'),
        api.get('/payouts/connect/status'),
      ]);
      if (e.status === 'fulfilled') setEarnings(e.value.data);
      if (c.status === 'fulfilled') setConnect(c.value.data);
    } finally {
      setLoading(false);
    }
  };

  const setupPayouts = async () => {
    setSettingUp(true);
    try {
      const { data } = await api.post('/payouts/connect/start');
      window.location.href = data.url; // Stripe-hosted Express onboarding
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payout setup');
      setSettingUp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const status = connect?.status || 'not_started';
  const isEnabled = status === 'enabled';

  const renderPayoutCard = () => {
    if (isEnabled) {
      return (
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-8 w-8 text-green-500 flex-shrink-0" />
          <div>
            <p className="text-lg font-semibold text-green-500">Payouts active</p>
            <p className="text-sm text-muted-foreground mt-1">
              Your share of each booking (you keep 85% of the service plus 100% of travel) is sent to your
              bank automatically by Stripe — no manual withdrawals needed.
            </p>
          </div>
        </div>
      );
    }

    if (status === 'pending_verification') {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <Clock className="h-8 w-8 text-primary flex-shrink-0" />
            <div>
              <p className="text-lg font-semibold">Payouts under review</p>
              <p className="text-sm text-muted-foreground mt-1">
                Stripe is verifying your details. This is usually quick. You can add or fix anything if needed.
              </p>
            </div>
          </div>
          <Button onClick={setupPayouts} disabled={settingUp} className="btn-primary w-full py-6 text-lg">
            {settingUp ? 'Opening…' : 'Review payout details'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      );
    }

    // not_started or onboarding
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <Landmark className="h-8 w-8 text-primary flex-shrink-0" />
          <div>
            <p className="text-lg font-semibold">
              {status === 'onboarding' ? 'Finish setting up payouts' : 'Set up payouts to get paid'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Onda pays you through Stripe. You'll add your bank and verify your identity on Stripe's secure
              page — then your earnings land in your account automatically. <strong>Clients can only book you
              once this is done.</strong>
            </p>
          </div>
        </div>
        <Button onClick={setupPayouts} disabled={settingUp} className="btn-primary w-full py-6 text-lg" data-testid="setup-payouts-btn">
          {settingUp ? 'Opening…' : (status === 'onboarding' ? 'Continue setup' : 'Set up payouts')}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="container mx-auto max-w-6xl">
        <h1 className="font-cormorant text-6xl font-bold mb-8 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Earnings</h1>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="luxury-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <p className="text-4xl font-bold font-cormorant">${earnings?.total_earnings || 0}</p>
              <p className="text-xs text-muted-foreground mt-2">Your share of completed bookings (after Onda's 15% fee).</p>
            </CardContent>
          </Card>

          <Card className="luxury-card border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Pending</p>
                <DollarSign className="h-5 w-5 text-primary animate-pulse" />
              </div>
              <p className="text-4xl font-bold font-cormorant text-primary">${earnings?.pending_earnings || 0}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Paid by the client — released to your balance when the job is marked completed
                ({earnings?.pending_bookings || 0} booking{(earnings?.pending_bookings || 0) === 1 ? '' : 's'}).
              </p>
            </CardContent>
          </Card>

          <Card className="luxury-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Completed Bookings</p>
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <p className="text-4xl font-bold font-cormorant">{earnings?.total_bookings || 0}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="luxury-card">
          <CardHeader>
            <CardTitle className="font-cormorant text-3xl">Payouts</CardTitle>
            <p className="text-sm text-muted-foreground">Secure payouts powered by Stripe</p>
          </CardHeader>
          <CardContent>
            {renderPayoutCard()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
